import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import FolderPanel from '~/components/Shared/FolderPanel/FolderPanel';
import { Box } from '~/eds/src/components/Box/Box';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Folder } from '~/eds/src/types/Folder';
import { BulkActionType } from '~/enums/BulkActionType';
import { api } from '~/redux/api/createApi';
import { capitalize } from '~/utils/strings';

import { BaseBulkActionProps } from '../types/bulk-actions';
import { getPanelText } from './utils';

interface Props extends BaseBulkActionProps {
  shouldDisableSyncPairedFolders: boolean;
  selectedCount: number;
  activePanel?: BulkActionType;
  onHide: () => void;
}
export const MoveCopyActionPanel = ({
  activePanel = 'move',
  isAllSelected = false,
  searchAPI,
  shouldDisableSyncPairedFolders,
  query = {},
  selectedCount,
  selectedDocumentIds = [],
  onActionCompleted,
  onHide,
}: Props) => {
  const isCopy = activePanel === 'copy';
  const apiName = isCopy ? 'searchV2BulkCopy' : 'searchV2BulkMove';
  const intl = useIntl();

  const [bulkAction, { isLoading }] = api.endpoints[apiName].useMutation();
  const [selectedFolder, setSelectedFolder] = useState<Folder | undefined>();
  const { toast } = useToast();

  const onSaveClick = async () => {
    if (selectedFolder) {
      try {
        await bulkAction({
          destinationFolder: selectedFolder.id,
          isAllSelected,
          searchAPI,
          searchQuery: query,
          documentHandlerIds: selectedDocumentIds,
        }).unwrap();
        toast({
          status: 'success',
          message: isCopy
            ? intl.formatMessage(
                {
                  id: 'MoveCopyActionPanel.copySuccessMessage',
                  defaultMessage:
                    '{selectedCount} items have been copied to {folderPath}',
                  description:
                    'Success message when items are copied. Example: selectedCount = 5, folderPath = "/My Folder"',
                },
                {
                  selectedCount,
                  folderPath: selectedFolder?.path,
                },
              )
            : intl.formatMessage(
                {
                  id: 'MoveCopyActionPanel.moveSuccessMessage',
                  defaultMessage:
                    '{selectedCount} items have been moved to {folderPath}',
                  description:
                    'Success message when items are moved. Example: selectedCount = 5, folderPath = "/My Folder"',
                },
                {
                  selectedCount,
                  folderPath: selectedFolder?.path,
                },
              ),
        });
        onActionCompleted?.();
      } catch {
        toast({
          status: 'danger',
          message: isCopy
            ? intl.formatMessage({
                id: 'MoveCopyActionPanel.copyErrorMessage',
                defaultMessage:
                  'An error occurred while copying the document(s).',
                description: 'Error message when copy operation fails',
              })
            : intl.formatMessage({
                id: 'MoveCopyActionPanel.moveErrorMessage',
                defaultMessage:
                  'An error occurred while moving the document(s).',
                description: 'Error message when move operation fails',
              }),
        });
      }
    }
  };
  const text = getPanelText(intl, activePanel, selectedCount);

  const footer = {
    actions: [
      {
        text: capitalize(activePanel),
        level: 'primary' as const,
        onClick: onSaveClick,
        disabled: !selectedFolder,
        isLoading,
      },
    ],
  };

  return (
    <Panel
      position="right"
      enableBackdrop
      width="m"
      hidden={{ isHidden: false, onHide }}
      title={
        isCopy
          ? intl.formatMessage({
              id: 'MoveCopyActionPanel.copyToFolderTitle',
              defaultMessage: 'Copy to Folder',
              description: 'Title for copy to folder panel',
            })
          : intl.formatMessage({
              id: 'MoveCopyActionPanel.moveToFolderTitle',
              defaultMessage: 'Move to Folder',
              description: 'Title for move to folder panel',
            })
      }
      footer={footer}
    >
      <Layout direction="column" spacing={2}>
        <Box px={4}>
          <Markdown text={text} />
        </Box>
        <Box w="100%" h="100%">
          <FolderPanel
            selectedFolder={selectedFolder}
            onSelect={setSelectedFolder}
            folderPathId={selectedFolder?.id as number}
            isRootFolder
            disableSyncPaired={shouldDisableSyncPairedFolders}
          />
        </Box>
      </Layout>
    </Panel>
  );
};
